import { forwardRef } from 'react';
import { Badge, type BadgeProps } from 'antd';
import { css } from '@emotion/react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { typeStyle } from 'constants/typography';

export interface CounterBadgeProps extends BadgeProps {
  withoutMargin?: boolean;
}

// Forwards the ref as antd's Badge does: a Tooltip anchors on its child's ref.
const CounterBadge = styled(
  forwardRef<HTMLSpanElement, CounterBadgeProps>(function CounterBadgeChip({ withoutMargin, ...props }, ref) {
    /**
     * The styled block below paints the chip, so this sets no `color` and no inline height: antd turns
     * `color` into an inline background, and neither can be overridden from CSS without `!important`.
     */
    return <Badge ref={ref} showZero overflowCount={999} {...props} />;
  })
)`
  ${(props: CounterBadgeProps) => css`
    /* Nav / Tab spaces the label and its counter at 6px (152:6, gap spacing-xxs). */
    margin-left: ${props.withoutMargin ? 0 : 'var(--spacing-xxs)'};

    /* Nav / Tab's Counter (266:5478): neutral fill, radius-100, 12px label on an 18px chip —
           the count carries no active tint of its own in the frames; the tab's label and underline
           carry the state. antd rounds its count into a pill and rings it with a white box-shadow;
           both go. */
    .ant-badge-count {
      min-width: 22px;
      height: 18px;
      padding: 0 var(--padding-xxs);
      border-radius: var(--border-radius-100);
      box-shadow: none;
      ${typeStyle.labelSm}
      background: var(--surface-secondary-background);
      color: var(--text-body);
    }
    /* Flex-centred, with the scroll units pinned to the chip height: antd's 20px digit column
       overflowed the chip, so the number rode the top edge (UI-polish review, "no top-bottom
       padding", "number is not centered"). The unit height must equal the chip height or antd's
       translateY(-N x 100%) lands on the wrong digit. antd sets a standalone count to display: block
       through .ant-badge-not-a-wrapper, one class deeper than a bare .ant-badge-count, so this matches
       those classes: a zero has no units, and without the flex it rides the top edge. */
    &.ant-badge.ant-badge-not-a-wrapper .ant-badge-count {
      display: flex;
      align-items: center;
      justify-content: center;
    }
    /* By class alone: antd 5 renders the units as spans, so a p-qualified selector, antd's own included,
       never matches. Each unit centres its digit, so the label keeps the role's line height. */
    .ant-scroll-number-only,
    .ant-scroll-number-only-unit {
      height: 18px;
    }
    .ant-scroll-number-only-unit {
      display: flex;
      align-items: center;
    }
  `}
`;

export default observer(CounterBadge);
